import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './MenuBrowser.css';
import { trackPageView } from '../lib/supabase';
import { IS_IN_IFRAME } from '../utils/iframeDetector';
import { findItem, isVisibleTo, routeForTab, routeForContent, buildSearchIndex, routeForSearchResult } from '../lib/menuTree';
import CardImage from './CardImage';

function ContentCard({ item, onOpen }) {
  return (
    <div
      className="content-card"
      onClick={() => onOpen(item)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { onOpen(item); e.preventDefault(); } }}
      tabIndex="0"
      role="listitem"
      aria-label={item.name}
    >
      <CardImage src={item.image} alt="" />
      <div className="content-title">{item.name}</div>
    </div>
  );
}

/**
 * The browse screen: category tabs, a search box, and a grid of cards for the
 * home page, a category or a subcategory. Each visit to one of these screens
 * records one page view; clicks only navigate.
 */
function MenuBrowser({ data }) {
  const navigate = useNavigate();
  const { categoryId, subcategoryId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');

  // One page view per screen visited
  useEffect(() => {
    if (!categoryId) {
      trackPageView('Home', null, null);
      return;
    }
    const cat = findItem(data, { categoryId });
    if (!cat) return;
    if (subcategoryId) {
      const sub = findItem(data, { categoryId, subcategoryId });
      if (sub) trackPageView(sub.name, cat.id, sub.id);
    } else {
      trackPageView(cat.name, cat.id, null);
    }
  }, [categoryId, subcategoryId, data]);

  // Redirect movies subcategory URLs to the /movies route
  useEffect(() => {
    if (!categoryId || !subcategoryId) return;
    if (findItem(data, { categoryId })?.type === 'movies') {
      navigate(`/movies/${categoryId}/subcategory/${subcategoryId}`, { replace: true });
    }
  }, [categoryId, subcategoryId, data, navigate]);

  // ── Derived values ───────────────────────────────────────────

  const facilityType =
    new URLSearchParams(window.location.search).get('type') ||
    sessionStorage.getItem('facilityType');

  const categories = useMemo(
    () => data.categories.filter(isVisibleTo(facilityType)),
    [data.categories, facilityType]
  );

  // Active tab derived directly from the URL
  const activeCategoryId = categoryId ? parseInt(categoryId, 10) : null;
  const activeCategory = activeCategoryId
    ? (categories.find(c => c.id === activeCategoryId)?.name ?? 'All')
    : 'All';
  const isHome = activeCategory === 'All';

  const categoryTabs = useMemo(
    () => [{ id: null, name: 'All' }, ...categories.map(c => ({ id: c.id, name: c.name }))],
    [categories]
  );

  const searchIndex = useMemo(() => buildSearchIndex(categories, facilityType), [categories, facilityType]);

  const contentItems = useMemo(() => {
    const visible = isVisibleTo(facilityType);
    if (subcategoryId) {
      return (findItem(data, { categoryId, subcategoryId })?.subcategories ?? []).filter(visible);
    }
    if (isHome) return categories;
    const selected = categories.find(c => c.id === activeCategoryId);
    return (selected?.subcategories ?? []).filter(visible);
  }, [subcategoryId, isHome, activeCategoryId, categoryId, categories, data, facilityType]);

  const searchResults = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return query ? searchIndex.filter(item => item.name.toLowerCase().includes(query)) : [];
  }, [searchIndex, searchQuery]);

  // ── Handlers ─────────────────────────────────────────────────

  const handleTabClick = (tab) => {
    setSearchQuery('');
    if (tab.id === null) { navigate('/'); return; }
    navigate(routeForTab(categories.find(c => c.id === tab.id), tab.id));
  };

  const handleContentClick = (item) => {
    navigate(routeForContent({ item, isHome, categories, activeCategoryId, categoryId, subcategoryId }));
  };

  const handleSearchResultClick = (item) => {
    setSearchQuery('');
    navigate(routeForSearchResult(item));
  };

  const handleBackNavigation = () => {
    if (subcategoryId) navigate(`/category/${categoryId}`);
    else if (categoryId) navigate('/');
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className={`menu-browser ${IS_IN_IFRAME ? 'iframe-mode' : 'standalone-mode'}`} role="main">
      {/* Category tabs */}
      <nav className="category-nav" aria-label="Main Categories">
        {categoryTabs.map(tab => (
          <button
            key={tab.name}
            className={`category-tab ${activeCategory === tab.name ? 'active-tab' : ''}`}
            onClick={() => handleTabClick(tab)}
            aria-current={activeCategory === tab.name ? 'page' : false}
          >
            {tab.name}
          </button>
        ))}
      </nav>

      {/* Search bar row — back button on left (when navigating deeper), search input fills rest */}
      {!IS_IN_IFRAME && (
        <div className="search-bar-wrapper">
          {(categoryId || subcategoryId) && !searchQuery && (
            <button className="back-arrow" onClick={handleBackNavigation} aria-label="Go back">
              <span aria-hidden="true">←</span> Back
            </button>
          )}
          <div className="search-bar-input-row">
            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search for games, music, movies..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              aria-label="Search all content"
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => setSearchQuery('')} aria-label="Clear search">✕</button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <section
        className="content-section"
        aria-label={searchQuery.trim() ? 'Search Results' : isHome ? 'All Content' : `${activeCategory} Content`}
      >
        {searchQuery.trim() ? (
          <>
            <p className="search-results-header">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
            </p>
            {searchResults.length > 0 ? (
              <div className="content-grid" role="list" aria-label="Search results">
                {searchResults.map(item => (
                  <ContentCard
                    key={`${item._catId}-${item._subId ?? 0}-${item._subSubId ?? 0}-${item.id}`}
                    item={item}
                    onOpen={handleSearchResultClick}
                  />
                ))}
              </div>
            ) : (
              <div className="no-content" role="status">No results found for &ldquo;{searchQuery}&rdquo;</div>
            )}
          </>
        ) : (
          <div className="content-grid" role="list" aria-label="Content items">
            {contentItems.length > 0 ? (
              contentItems.map(item => (
                <ContentCard
                  key={`${categoryId ?? 'home'}-${subcategoryId ?? 'root'}-${item.id}`}
                  item={item}
                  onOpen={handleContentClick}
                />
              ))
            ) : (
              <div className="no-content" role="status">No content available</div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default MenuBrowser;
